/* useSupplierBranding — when an authenticated buyer is operating in
 * the context of a specific supplier (verqo is a closed 2-way network),
 * we re-skin the browser-chrome so the buyer's tab + favicon read
 * "they're inside the seller's portal" — not "they're inside verqo
 * looking at a seller."
 *
 * What this hook does:
 *   1. Sets `document.title` to "{Seller name} · {Page label}" when a
 *      supplier is active. Falls back to the platform default when not.
 *   2. Swaps the favicon to the seller's square logo (or primary logo
 *      as a fallback) when supplier context is active. Reverts to the
 *      verqo favicon on unmount / supplier change to None.
 *
 * Usage (anywhere inside the supplier-scoped buyer portal):
 *   useSupplierBranding("Orders");   // → tab: "GlowCo Beauty · Orders"
 *
 * Or mount once at the layout level (PortalShell) and pass per-page
 * labels via React Router data attrs — the simplest path is calling it
 * on each buyer page with the page name. Trade-off: a few extra lines
 * per page but no router gymnastics.
 */
import { useEffect } from "react";
import { useSupplier } from "../context/SupplierContext";

const PLATFORM_TITLE_DEFAULT = "verqo · Wholesale, simplified.";
const PLATFORM_FAVICON_DEFAULT = "/favicon.svg";

// Selector for the favicon link element. CRA boilerplate ships an SVG
// favicon; we mutate its `href` rather than insert a new `<link>` so the
// browser doesn't end up with two competing icons.
const FAVICON_SELECTOR = 'link[rel="icon"]';

function findFaviconLink() {
  let link = document.querySelector(FAVICON_SELECTOR);
  if (!link) {
    // Defensive — some build configs strip the boilerplate. Create one.
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  return link;
}

/**
 * Resolve the buyer-context favicon for an active supplier.
 *
 * Priority order:
 *   1. `logo_square_url` — explicit square/monogram variant the seller
 *      uploaded for exactly this purpose. Always best.
 *   2. Auto-generated monogram (first letter of seller name, white-on-
 *      primary-color, rendered to a 64×64 PNG via canvas → data URL).
 *      Looks clean in the browser tab without forcing the seller to
 *      upload a separate square asset.
 *
 * IMPORTANT: we deliberately do NOT fall back to `logo_url` here.
 * Primary logos are typically wide horizontal artwork (1600×400-ish);
 * browsers squish them into 16×16 tab icons and you get an
 * unrecognisable vertical sliver. Better to render a clean monogram.
 *
 * Returns a URL string (or data: URI), or null when no seller context.
 */
function generateMonogramFavicon(seller) {
  if (!seller) return null;
  const name = (seller.tenant_name || seller.name || "?").trim();
  if (!name) return null;
  // First non-whitespace character, uppercase.
  const letter = name.charAt(0).toUpperCase();
  const primary = (seller.branding && seller.branding.primary_color) || "#0F766E";

  try {
    const size = 64;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    // Rounded square background in the seller's primary color
    const r = 12;
    ctx.fillStyle = primary;
    ctx.beginPath();
    ctx.moveTo(r, 0);
    ctx.lineTo(size - r, 0);
    ctx.quadraticCurveTo(size, 0, size, r);
    ctx.lineTo(size, size - r);
    ctx.quadraticCurveTo(size, size, size - r, size);
    ctx.lineTo(r, size);
    ctx.quadraticCurveTo(0, size, 0, size - r);
    ctx.lineTo(0, r);
    ctx.quadraticCurveTo(0, 0, r, 0);
    ctx.closePath();
    ctx.fill();
    // White centered letter
    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 40px -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    // Optical alignment — letterforms hang slightly low when centered to
    // the geometric middle, so nudge up by 2px.
    ctx.fillText(letter, size / 2, size / 2 + 2);
    return canvas.toDataURL("image/png");
  } catch {
    return null;
  }
}

function resolveFavicon(active) {
  const b = (active && active.branding) || {};
  // Only honor an EXPLICIT square asset. Wide horizontal logos look
  // terrible squished into a 16×16 tab icon — we generate a monogram
  // instead in those cases (handled by the caller).
  return b.logo_square_url || null;
}

export function useSupplierBranding(pageLabel) {
  const { active } = useSupplier() || {};
  const sellerName = (active && (active.tenant_name || active.name)) || null;

  useEffect(() => {
    // ── Tab title ──
    // "GlowCo Beauty · Orders" when both present;
    // "GlowCo Beauty" when only the seller name is known;
    // platform default otherwise.
    const prevTitle = document.title;
    if (sellerName && pageLabel) {
      document.title = `${sellerName} · ${pageLabel}`;
    } else if (sellerName) {
      document.title = sellerName;
    } else if (pageLabel) {
      document.title = `${pageLabel} · verqo`;
    } else {
      document.title = PLATFORM_TITLE_DEFAULT;
    }

    // ── Favicon ──
    const link = findFaviconLink();
    const prevHref = link.getAttribute("href");
    let next = resolveFavicon(active);
    let isDataUri = false;
    // No explicit square asset uploaded → generate a clean monogram.
    // This sidesteps the "wide logo squished into 16×16" problem entirely.
    if (!next && active) {
      next = generateMonogramFavicon(active);
      isDataUri = !!next;
    }
    if (next) {
      if (isDataUri) {
        // Canvas-generated PNG — no cache-buster needed (data URI is its
        // own immutable identifier).
        link.setAttribute("href", next);
        link.setAttribute("type", "image/png");
      } else {
        // Server-hosted asset — append a stable suffix so the browser
        // picks up the swap (favicons are aggressively cached).
        const stamp = (active && (active.tenant_id || active.id)) || "x";
        link.setAttribute("href", `${next}#sup-${stamp}`);
        link.setAttribute("type",
          /\.svg(\?|#|$)/i.test(next) ? "image/svg+xml" : "image/png");
      }
    } else {
      // No supplier context: restore the platform default.
      link.setAttribute("href", PLATFORM_FAVICON_DEFAULT);
      link.setAttribute("type", "image/svg+xml");
    }

    return () => {
      // On unmount / route change, restore the previous values. This
      // prevents the seller's branding from "sticking" when the user
      // navigates back to a platform-level page.
      document.title = prevTitle;
      if (prevHref) {
        link.setAttribute("href", prevHref);
      }
    };
  }, [sellerName, pageLabel, active]);
}

export default useSupplierBranding;
